import React from 'react';

import SchoolOption from './school_option';
import SchoolNotListedModal from './school_not_listed_modal'

import { requestGet, requestPost, } from '../../../../modules/request';
import { COLLEGE_INSTITUTION_TYPE, COLLEGE_NOT_LISTED, COLLEGE_NO_SCHOOL_SELECTED, Input, NOT_LISTED, NO_SCHOOL_SELECTED, smallWhiteCheckIcon, Spinner } from '../../../Shared/index';
import useDebounce from '../../hooks/useDebounce';

const mapSearchSrc = `${window.postbuildEnv.CDN_URL}/images/onboarding/map-search.svg`

const DEBOUNCE_LENGTH = 500
const MINIMUM_SEARCH_LENGTH = 2
const WHOLE_SEARCH_IS_NUMBERS_REGEX = /^\d+$/

const SchoolSelector = ({ children, selectSchool, showDismissSchoolSelectionReminderCheckbox, handleDismissSchoolSelectionReminder, disableSkipForNow, isCollege}) => {
  const [search, setSearch] = React.useState('')
  const [schools, setSchools] = React.useState([])
  const [errors, setErrors] = React.useState({})
  const [loading, setLoading] = React.useState(false)
  const [showNotListedModal, setShowNotListedModal] = React.useState(false)
  const [unlistedSchoolName, setUnlistedSchoolName] = React.useState('')
  const [unlistedSchoolZipcode, setUnlistedSchoolZipcode] = React.useState('')
  const [dismissSchoolSelectionReminder, setDismissSchoolSelectionReminder] = React.useState(false)

  const institutionNoun = isCollege ? 'institution' : 'school'

  const debouncedSearch = useDebounce(search, DEBOUNCE_LENGTH);
  const requestKeyRef = React.useRef('')

  React.useEffect(() => {
    if (search.length >= MINIMUM_SEARCH_LENGTH) {
      setLoading(true)
    }
  }, [search])

  React.useEffect(() => {
    setSchools([])
    if (search.length >= MINIMUM_SEARCH_LENGTH) {
      setLoading(true)
    }
  }, [isCollege])

  React.useEffect(() => {
    if (search.length < MINIMUM_SEARCH_LENGTH) { return }
    // if they're typing a zip code, don't search until the full zip code is entered
    if (search.match(WHOLE_SEARCH_IS_NUMBERS_REGEX) && search.length < 5) { return }

    searchForSchool()
  }, [debouncedSearch, isCollege])

  function toggleDismissReminderCheckbox() { setDismissSchoolSelectionReminder(!dismissSchoolSelectionReminder) }

  function handleSkipClick() {
    if (dismissSchoolSelectionReminder) {
      handleDismissSchoolSelectionReminder()
    }
    selectSchool(isCollege ? COLLEGE_NO_SCHOOL_SELECTED : NO_SCHOOL_SELECTED)
  }

  function handleNotListedClick() {
    setShowNotListedModal(true)
  }

  function hideSchoolNotListedModal() { setShowNotListedModal(false) }

  function onChangeUnlistedSchoolName(e) { setUnlistedSchoolName(e.target.value) }
  function onChangeUnlistedSchoolZipcode(e) { setUnlistedSchoolZipcode(e.target.value) }

  function submitSchoolNotListedInformation() {
    requestPost('/submit_unlisted_school_information', { school_name: unlistedSchoolName, school_zipcode: unlistedSchoolZipcode }, () => {
      selectSchool(isCollege ? COLLEGE_NOT_LISTED : NOT_LISTED)
    })
  }

  function searchForSchool() {
    const requestKey = `${isCollege}:${search}`
    requestKeyRef.current = requestKey
    const institutionTypeParam = isCollege ? `&institution_type=${COLLEGE_INSTITUTION_TYPE}` : ''
    requestGet(
      `${window.postbuildEnv.DEFAULT_URL}/schools?search=${search}${institutionTypeParam}`,
      (body) => {
        if (requestKeyRef.current !== requestKey) { return }

        setSchools(body.data)
        setLoading(false)
      }
    )
  }

  function updateSearch(e) {
    setSearch(e.target.value)
  }

  const renderLoading = () => {
    return (
      <div className="loading">
        <Spinner />
      </div>
    )
  }

  const renderDefault = () => {
    return (
      <div className="default-school-search">
        <img alt="Map with a magnifying glass over it" src={mapSearchSrc} />
        <p className="message">Search for your {institutionNoun} above</p>
      </div>
    )
  }

  const renderSchoolNotListedModal = () => {
    return (
      <SchoolNotListedModal
        close={hideSchoolNotListedModal}
        onChangeUnlistedSchoolName={onChangeUnlistedSchoolName}
        onChangeUnlistedSchoolZipcode={onChangeUnlistedSchoolZipcode}
        show={showNotListedModal}
        submitSchoolNotListedInformation={submitSchoolNotListedInformation}
        unlistedSchoolName={unlistedSchoolName}
        unlistedSchoolZipcode={unlistedSchoolZipcode}
      />
    )
  }

  const noSchoolSelectedSchoolOption = (
    <SchoolOption
      key={NOT_LISTED}
      numberOfTeachersText=''
      school={{id: NOT_LISTED}}
      secondaryText={`Please select this if you can't find your ${institutionNoun}`}
      selectSchool={handleNotListedClick}
      text="Not listed"
    />
  )

  const renderSchoolsList = (schools) => {
    const schoolItems = schools.map(school => {
      const { city, number_of_teachers, state, street, text, zipcode } = school.attributes
      const numberOfTeachers = number_of_teachers
      let secondaryText = ''
      let numberOfTeachersText = ''
      if (street) {
        secondaryText = `${street}, `
      }
      if (city) {
        secondaryText += `${city}, `
      }
      if (state) {
        secondaryText += `${state} `
      }
      if (zipcode) {
        secondaryText += zipcode
      }
      if (numberOfTeachers) {
        numberOfTeachersText = `${numberOfTeachers} Quill Teacher${numberOfTeachers === 1 ? '' : 's'}`
      }
      return (
        <SchoolOption
          key={school.id}
          numberOfTeachersText={numberOfTeachersText}
          school={school}
          secondaryText={secondaryText}
          selectSchool={selectSchool}
          text={text}
        />
      )
    })
    return <ul className="list quill-list double-line">{schoolItems}{noSchoolSelectedSchoolOption}</ul>
  }

  const skipForNowCheckbox = () => {
    if (disableSkipForNow) { return }

    let checkbox = (
      <button
        aria-checked={false}
        aria-label="Unchecked"
        className="quill-checkbox unselected focus-on-light"
        onClick={toggleDismissReminderCheckbox}
        role="checkbox"
        type="button"
      />
    )

    if (dismissSchoolSelectionReminder) {
      checkbox = (
        <button
          aria-checked={true}
          className="quill-checkbox selected focus-on-light"
          onClick={toggleDismissReminderCheckbox}
          role="checkbox"
          type="button"
        >
          <img alt={smallWhiteCheckIcon.alt} src={smallWhiteCheckIcon.src} />
        </button>
      )
    }

    const checkboxWrapper = showDismissSchoolSelectionReminderCheckbox
      ? <div className="checkbox-wrapper">{checkbox} <span>Don&#39;t remind me again to select a school</span></div>
      : <span />

    return (
      <div className="school-not-listed">
        <button className="quill-button outlined green extra-small focus-on-light" onClick={handleSkipClick} type="button">Skip for now</button>
        {checkboxWrapper}
      </div>
    )
  }

  const renderSchoolsListSection = () => {
    let title
    let schoolsListOrEmptyState

    if (loading) {
      schoolsListOrEmptyState = renderLoading()
    } else if (search.length >= MINIMUM_SEARCH_LENGTH) {
      schoolsListOrEmptyState = renderSchoolsList(schools)
    } else {
      schoolsListOrEmptyState = renderDefault()
    }

    return (
      <div className="schools-list-section">
        <div className="title">Results</div>
        {schoolsListOrEmptyState}
        {skipForNowCheckbox()}
      </div>
    )
  }

  return (
    <div className="school-search-container">
      {renderSchoolNotListedModal()}
      {children}
      <Input
        className="school-selection-search search bordered small"
        error={errors.search}
        handleChange={updateSearch}
        id="search"
        label={`Search by ${institutionNoun} name or zip code`}
        type="text"
        value={search}
      />
      {renderSchoolsListSection()}
    </div>
  )
}

export default SchoolSelector
